import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Routes } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import { MicroUiMoleculeModule } from '@micro-ui/molecule';
import { BrightsideUiModule } from '@brightside/brightside-ui';
import { SharedDesktopModule, DisplayMessageComponent } from '@brightside-web/shared/desktop';
import { SharedDisplayCampaignComponent, SharedDisplayModule } from '@brightside-web/shared/display';
import { CreditSetupService } from '@brightside-web/desktop/data-access/credit';

import { CreationComponent } from './creation/creation.component';
import { CreationWaitingComponent } from './creation-waiting/creation-waiting.component';
import { VerifyOtpComponent } from './verfiy-otp/verify-otp.component';
import { VerifyKbaIntroComponent } from './verfiy-kba/intro/verify-kba-intro.component';
import { VerifyKbaComponent } from './verfiy-kba/verify-kba.component';

import { InSetupGuard } from './in-setup.guard';
import { InTermsGuard } from './in-terms.guard';
import { InVerifyGuard } from './in-verify.guard';
import { SkipTermsGuard } from './skip-terms.guard';
import { VerifyOtpGuard } from './verify-otp.guard';
import { VerifyKbaGuard } from './verify-kba.guard';
import { ConsentManager, ConsentType, SharedConsentsComponent } from '@brightside-web/shared/consents';
import { TranslateModule } from '@ngx-translate/core';
import { VerifySmfaComponent } from './verify-smfa/verify-smfa.component';
import { DocumentsKeyEnum, RequiredLegalDocumentsComponent } from '@brightside-web/shared/legal-documents';
import { CreditSetupErrorComponent } from './credit-setup-error/credit-setup-error.component';

const BREADCRUMB_LABEL = 'Connect credit report';

const routes: Routes = [
  {
    path: 'creation',
    canActivate: [InSetupGuard],
    component: CreationComponent,
    data: { breadcrumb: BREADCRUMB_LABEL },
  },
  {
    path: 'creation-waiting',
    canActivate: [InTermsGuard],
    component: CreationWaitingComponent,
    data: { breadcrumb: BREADCRUMB_LABEL },
  },
  {
    path: 'consents',
    component: RequiredLegalDocumentsComponent,
    canActivate: [SkipTermsGuard],
    data: {
      pageConfig: {
        documentsKey: DocumentsKeyEnum.CREDIT_LINKING,
        closeButtonRoute: ['credit-setup', 'creation'],
        pageOnForwardCtaPath: ['credit-setup', 'creation-waiting'],
      },
    },
  },
  {
    path: 'verify',
    canActivate: [InVerifyGuard],
    data: { breadcrumb: { skip: true } },
    children: [
      { path: 'smfa', component: VerifySmfaComponent, canActivate: [], data: { breadcrumb: BREADCRUMB_LABEL } },
      { path: 'otp', component: VerifyOtpComponent, canActivate: [VerifyOtpGuard], data: { breadcrumb: BREADCRUMB_LABEL } },
      {
        path: 'kba',
        canActivate: [VerifyKbaGuard],
        data: {
          breadcrumb: { skip: true },
          checkStatusWith: [{ validator: CreditSetupService.isVerifyKba, invalidPath: ['home'] }],
        },
        children: [
          { path: 'intro', component: VerifyKbaIntroComponent, data: { breadcrumb: BREADCRUMB_LABEL } },
          { path: 'questions', component: VerifyKbaComponent, data: { breadcrumb: BREADCRUMB_LABEL } },
        ],
      },
    ],
  },
  {
    path: 'complete-onboarding',
    canActivate: [],
    component: DisplayMessageComponent,
    data: {
      breadcrumb: BREADCRUMB_LABEL,
      pageConfig: {
        fbEventName: 'onboarding_credit_completed',
        pageTitleKey: 'ONBOARDING_CREDIT_VERIFIED_SUCCESS_TITLE',
        pageTitleImage: 'thumbs-up.png',
        pageSubTitleKey: 'ONBOARDING_CREDIT_VERIFIED_SUCCESS_DESC',
        pageCtaLabelKey: 'BUTTON_CONTINUE',
        pageOnForwardCtaPath: ['onboarding'],
        pageOnForwardCtaData: { updateStepWithStatus: 'success' },
      },
    },
  },
  {
    path: 'error-onboarding',
    canActivate: [],
    component: DisplayMessageComponent,
    data: {
      breadcrumb: BREADCRUMB_LABEL,
      pageConfig: {
        fbEventName: 'onboarding_credit_error',
        pageTitleKey: 'ONBOARDING_ERROR_CREDIT_FAILED_TITLE',
        pageTitleImage: 'warning-exclamation.png',
        pageSubTitleKey: 'ONBOARDING_ERROR_CREDIT_FAILED_DESC',
        pageCtaLabelKey: 'BUTTON_CONTINUE',
        pageOnForwardCtaPath: ['onboarding'],
        pageOnForwardCtaData: { updateStepWithStatus: 'failed' },
      },
    }
  },
  {
    path: 'complete',
    canActivate: [],
    component: DisplayMessageComponent,
    data: {
      breadcrumb: BREADCRUMB_LABEL,
      pageConfig: {
        fbEventName: 'credit_completed',
        analytics: {
          shown: 'credit_setup_success_shown',
        },
        pageTitleKey: 'IDENTITY_CONFIRMED_TITLE',
        pageSubTitleKey: 'IDENTITY_CONFIRMED_DESC_WEB',
        pageCtaLabelKey: 'BUTTON_CONTINUE',
        pageOnForwardCtaPath: ['home'],
      },
    },
  },
  {
    path: 'credit-setup-error',
    component: CreditSetupErrorComponent,
    canActivate: [],
    data: { breadcrumb: BREADCRUMB_LABEL },
  }
];

@NgModule({
  imports: [
    BrightsideUiModule,
    CommonModule,
    FormsModule,
    MicroUiMoleculeModule,
    ReactiveFormsModule,
    SharedDesktopModule,
    SharedDisplayModule,
    TranslateModule,
    RouterModule.forChild(routes),
  ],
  declarations: [
    CreationComponent,
    CreationWaitingComponent,
    VerifyOtpComponent,
    VerifyKbaIntroComponent,
    VerifyKbaComponent,
    VerifySmfaComponent,
    CreditSetupErrorComponent,
  ],
})
export class DesktopFeatureCreditSetupModule {}
